import React, { useEffect, useState } from "react";
import { store } from "../../store";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { type SubmitHandler, useForm } from "react-hook-form";
import { ErrorText } from "@/components/ui/error-text";
import { rfc5322Email } from "@/lib/validation";

const ResetPasswordCompleted = (): JSX.Element => {
    const { t } = useTranslation();

    return (
        <div className="grid gap-4">
            <DialogHeader>
                <DialogTitle>{t("component.modalResetPassword.title")}</DialogTitle>
                <DialogDescription>{t("component.modalResetPassword.completed")}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline">{t("generic.close")}</Button>
                </DialogClose>
            </DialogFooter>
        </div>
    );
};

type Form = {
    email: string;
};

type ResetPasswordFormProps = {
    onComplete: () => void;
};

const ResetPasswordForm = observer(({ onComplete }: ResetPasswordFormProps): JSX.Element => {
    const { t } = useTranslation();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Form>();

    const [loading, setLoading] = useState(false);
    const [errored, setErrored] = useState(false);

    const handleResetPassword: SubmitHandler<Form> = async (form): Promise<void> => {
        setLoading(true);
        try {
            await store.emailLoginTokensApi.emailLoginTokensCreateEmailLoginToken({
                emailLoginTokenCreate: {
                    email: form.email,
                },
            });
            onComplete();
        } catch (e) {
            setErrored(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(handleResetPassword)} className="grid gap-4">
            <DialogHeader>
                <DialogTitle>{t("component.modalResetPassword.title")}</DialogTitle>
                <DialogDescription>
                    {t("component.modalResetPassword.description")}
                </DialogDescription>
            </DialogHeader>
            <div className="grid gap-2">
                <Label htmlFor="email">{t("formResetPassword.email")}</Label>
                <Input
                    {...register("email", {
                        required: {
                            value: true,
                            message: t("validation.required"),
                        },
                        pattern: {
                            value: rfc5322Email,
                            message: t("validation.invalid.email"),
                        },
                    })}
                    errored={errors.email != null}
                />
                {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
                {errored && <ErrorText>{t("generic.unknownError")}</ErrorText>}
            </div>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline">{t("generic.cancel")}</Button>
                </DialogClose>
                <Button disabled={loading} type="submit">
                    {t("generic.submit")}
                </Button>
            </DialogFooter>
        </form>
    );
});

export type ResetPasswordModalProps = {
    onOpenChange: (open: boolean) => void;
    open: boolean;
};

export const ResetPasswordModal = observer(
    ({ onOpenChange, open }: ResetPasswordModalProps): JSX.Element => {
        const [completed, setCompleted] = useState(false);

        // Reset state when the modal is closed.
        useEffect(() => {
            if (!open) {
                setCompleted(false);
            }
        }, [open]);

        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent>
                    {completed ? (
                        <ResetPasswordCompleted />
                    ) : (
                        <ResetPasswordForm onComplete={() => setCompleted(true)} />
                    )}
                </DialogContent>
            </Dialog>
        );
    },
);
